/* MistLab – 3D flacon (Three.js, procedural, no external models) */
import * as THREE from 'https://cdn.jsdelivr.net/npm/three@0.160.0/build/three.module.min.js';

const root = document.querySelector('[data-h3d]');
if (root) {
  try { init(root); } catch (e) { console.warn('[MistLab 3D]', e); root.classList.add('h3d--nogl'); }
}

function rrect(w, h, r) {
  const s = new THREE.Shape(), x = -w / 2, y = -h / 2;
  s.moveTo(x + r, y); s.lineTo(x + w - r, y); s.quadraticCurveTo(x + w, y, x + w, y + r);
  s.lineTo(x + w, y + h - r); s.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  s.lineTo(x + r, y + h); s.quadraticCurveTo(x, y + h, x, y + h - r);
  s.lineTo(x, y + r); s.quadraticCurveTo(x, y, x + r, y);
  return s;
}
function slab(w, h, d, r, bev, seg = 6) {
  const g = new THREE.ExtrudeGeometry(rrect(w, h, r), { depth: d, bevelEnabled: true, bevelThickness: bev, bevelSize: bev, bevelSegments: seg, curveSegments: 14 });
  g.center(); g.computeVertexNormals(); return g;
}
function envMap(renderer) {
  const s = new THREE.Scene(), box = new THREE.BoxGeometry(1, 1, 1);
  s.add(new THREE.Mesh(new THREE.BoxGeometry(14, 14, 14), new THREE.MeshBasicMaterial({ color: 0x0c0a08, side: THREE.BackSide })));
  const add = (c, k, p, sc) => { const m = new THREE.Mesh(box, new THREE.MeshBasicMaterial({ color: new THREE.Color(c).multiplyScalar(k) })); m.position.set(...p); m.scale.set(...sc); s.add(m); };
  add(0xfff3e0, 7, [0, 6, 0], [7, .1, 3]);
  add(0xffe2b8, 5, [-6.5, 1, 1.5], [.1, 6, 1.4]);
  add(0xffffff, 4, [6.5, .5, -1], [.1, 5, .9]);
  add(0xc9a86a, 2.2, [0, -1.5, -6.5], [9, 2.5, .1]);
  add(0xffe9c9, .9, [-4, -2, 6.5], [.4, 1.2, .1]);
  const pm = new THREE.PMREMGenerator(renderer), t = pm.fromScene(s, .02).texture; pm.dispose(); return t;
}
function bgTex() {
  const c = document.createElement('canvas'); c.width = c.height = 1024; const g = c.getContext('2d');
  const r = g.createRadialGradient(512, 470, 0, 512, 512, 720);
  r.addColorStop(0, '#2a2119'); r.addColorStop(.35, '#1a1511'); r.addColorStop(.75, '#0d0b0a'); r.addColorStop(1, '#0b0a09');
  g.fillStyle = r; g.fillRect(0, 0, 1024, 1024);
  const t = new THREE.CanvasTexture(c); t.colorSpace = THREE.SRGBColorSpace; return t;
}
function dotTex(soft) {
  const c = document.createElement('canvas'); c.width = c.height = 64; const g = c.getContext('2d');
  const r = g.createRadialGradient(32, 32, 0, 32, 32, 32);
  r.addColorStop(0, 'rgba(255,255,255,1)'); r.addColorStop(soft ? .25 : .35, 'rgba(255,255,255,' + (soft ? .35 : .8) + ')'); r.addColorStop(1, 'rgba(255,255,255,0)');
  g.fillStyle = r; g.fillRect(0, 0, 64, 64); const t = new THREE.CanvasTexture(c); return t;
}
async function labelTex() {
  try { await document.fonts.load('500 120px "Cormorant Garamond"'); await document.fonts.load('500 30px Manrope'); } catch (e) {}
  const c = document.createElement('canvas'); c.width = 1024; c.height = 400; const g = c.getContext('2d');
  g.fillStyle = '#000'; g.fillRect(0, 0, 1024, 400); g.fillStyle = '#fff'; g.strokeStyle = '#fff';
  g.lineWidth = 3; g.strokeRect(24, 24, 976, 352); g.lineWidth = 1.2; g.strokeRect(40, 40, 944, 320);
  g.textAlign = 'center'; g.textBaseline = 'middle';
  g.font = '500 150px "Cormorant Garamond", Georgia, serif';
  if ('letterSpacing' in g) g.letterSpacing = '28px';
  g.fillText('MISTLAB', 526, 180);
  g.font = '500 30px Manrope, Arial, sans-serif'; if ('letterSpacing' in g) g.letterSpacing = '14px';
  g.fillText('EAU DE PARFUM', 520, 300);
  const t = new THREE.CanvasTexture(c); t.anisotropy = 8; return t;
}
const ease = t => t < .5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
const lerp = (a, b, t) => a + (b - a) * t;
const clamp = (v, a = 0, b = 1) => Math.min(b, Math.max(a, v));

function init(root) {
  const canvas = root.querySelector('[data-h3d-canvas]');
  const test = document.createElement('canvas').getContext('webgl2') || document.createElement('canvas').getContext('webgl');
  if (!canvas || !test) { root.classList.add('h3d--nogl'); return; }
  const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const small = () => innerWidth < 900;

  const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true, powerPreference: 'high-performance' });
  renderer.setPixelRatio(Math.min(devicePixelRatio, small() ? 1.5 : 1.75));
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.ACESFilmicToneMapping; renderer.toneMappingExposure = 1.15;
  const scene = new THREE.Scene(); scene.environment = envMap(renderer);
  scene.background = bgTex();
  /* gold arch behind the bottle */
  const archPts = []; for (let a = 0; a <= 64; a++) { const t = Math.PI * a / 64; archPts.push(new THREE.Vector3(Math.cos(t) * 1.8, .85 + Math.sin(t) * 1.8, 0)); }
  archPts.unshift(new THREE.Vector3(1.8, -1.9, 0)); archPts.push(new THREE.Vector3(-1.8, -1.9, 0));
  const archMat = new THREE.LineBasicMaterial({ color: 0xc9a86a, transparent: true, opacity: .35 });
  const arch = new THREE.Group(); arch.position.z = -2.2;
  arch.add(new THREE.Line(new THREE.BufferGeometry().setFromPoints(archPts), archMat));
  const inner = new THREE.Line(new THREE.BufferGeometry().setFromPoints(archPts.map(v => new THREE.Vector3(v.x * .93, v.y > .85 ? .85 + (v.y - .85) * .93 : v.y, 0))), new THREE.LineBasicMaterial({ color: 0xc9a86a, transparent: true, opacity: .14 }));
  arch.add(inner); scene.add(arch);
  const camera = new THREE.PerspectiveCamera(30, 1, .1, 60); camera.position.set(0, .25, 9.6);

  const key = new THREE.SpotLight(0xffe6c2, 60, 30, .5, .8); key.position.set(-4, 6, 5); scene.add(key);
  const rim = new THREE.PointLight(0xc9a86a, 25, 20); rim.position.set(3.5, 1.5, -3); scene.add(rim);
  scene.add(new THREE.AmbientLight(0xffffff, .15));

  /* ---- bottle ---- */
  const chapters = [...root.querySelectorAll('[data-ch]')];
  const tints = chapters.map(c => new THREE.Color(c.dataset.tint || '#d9a45a'));
  const bottle = new THREE.Group(), stage = new THREE.Group(); stage.add(bottle); scene.add(stage);

  const glass = new THREE.MeshPhysicalMaterial({ color: 0xffffff, roughness: .03, transmission: 1, thickness: 1.4, ior: 1.52, clearcoat: 1, clearcoatRoughness: .02, attenuationColor: new THREE.Color(0xf6e9d2), attenuationDistance: 5, envMapIntensity: 1.35, specularIntensity: 1 });
  const body = new THREE.Mesh(slab(1.5, 1.9, .55, .22, .13), glass); bottle.add(body);
  const liquidMat = new THREE.MeshPhysicalMaterial({ color: tints[0].clone(), roughness: .18, metalness: 0, clearcoat: .6, emissive: tints[0].clone(), emissiveIntensity: .12, envMapIntensity: .9 });
  const liquid = new THREE.Mesh(slab(1.26, 1.3, .38, .16, .05), liquidMat); liquid.position.y = -.2; bottle.add(liquid);

  const gold = new THREE.MeshStandardMaterial({ color: 0xd4b27a, metalness: 1, roughness: .22, envMapIntensity: 1.3 });
  const lacquer = new THREE.MeshPhysicalMaterial({ color: 0x0d0c0b, roughness: .2, metalness: .3, clearcoat: 1, clearcoatRoughness: .05, envMapIntensity: 1.2 });
  const collar = new THREE.Mesh(new THREE.CylinderGeometry(.2, .26, .2, 64), gold); collar.position.y = 1.17; bottle.add(collar);
  const tube = new THREE.Mesh(new THREE.CylinderGeometry(.025, .025, 1.6, 12), new THREE.MeshStandardMaterial({ color: 0xffffff, transparent: true, opacity: .25, roughness: .1 })); tube.position.y = .3; bottle.add(tube);

  const cap = new THREE.Group(); cap.position.y = 1.64; bottle.add(cap);
  const capBody = new THREE.Mesh(slab(.86, .66, .5, .08, .05), lacquer); cap.add(capBody);
  const band = new THREE.Mesh(slab(.95, .05, .59, .02, .012, 2), gold); band.position.y = -.37; cap.add(band);
  const crest = new THREE.Mesh(new THREE.CylinderGeometry(.09, .09, .02, 48), gold); crest.rotation.x = Math.PI / 2; crest.position.set(0, .02, .305); cap.add(crest);

  const labelMat = new THREE.MeshStandardMaterial({ color: 0xd9bf88, metalness: 1, roughness: .28, transparent: true, depthWrite: false });
  const label = new THREE.Mesh(new THREE.PlaneGeometry(1.02, .4), labelMat); label.position.set(0, .12, .411); bottle.add(label);
  labelTex().then(t => { labelMat.alphaMap = t; labelMat.needsUpdate = true; });

  /* pedestal */
  const ped = new THREE.Group(); ped.position.y = -1.27; stage.add(ped);
  const pedTop = new THREE.Mesh(new THREE.CylinderGeometry(1.35, 1.35, .14, 96), lacquer); ped.add(pedTop);
  const pedRing = new THREE.Mesh(new THREE.TorusGeometry(1.35, .012, 12, 160), gold); pedRing.rotation.x = Math.PI / 2; pedRing.position.y = .07; ped.add(pedRing);
  const pedLow = new THREE.Mesh(new THREE.CylinderGeometry(1.5, 1.55, .09, 96), lacquer); pedLow.position.y = -.12; ped.add(pedLow);

  /* ---- particles: gold dust + spray mist ---- */
  const DUST = small() ? 140 : 280, MIST = small() ? 160 : 320;
  const dGeo = new THREE.BufferGeometry(), dPos = new Float32Array(DUST * 3), dSeed = new Float32Array(DUST);
  for (let i = 0; i < DUST; i++) { dPos[i * 3] = (Math.random() - .5) * 11; dPos[i * 3 + 1] = (Math.random() - .5) * 7; dPos[i * 3 + 2] = (Math.random() - .5) * 5 - 1; dSeed[i] = Math.random() * 10; }
  dGeo.setAttribute('position', new THREE.BufferAttribute(dPos, 3));
  const dust = new THREE.Points(dGeo, new THREE.PointsMaterial({ color: 0xe6cf9f, size: .045, map: dotTex(), transparent: true, opacity: .75, depthWrite: false, blending: THREE.AdditiveBlending }));
  scene.add(dust);

  const mGeo = new THREE.BufferGeometry(), mPos = new Float32Array(MIST * 3), mVel = new Float32Array(MIST * 3), mLife = new Float32Array(MIST).fill(0);
  mGeo.setAttribute('position', new THREE.BufferAttribute(mPos, 3));
  const mistMat = new THREE.PointsMaterial({ color: 0xfff4e2, size: .42, map: dotTex(true), transparent: true, opacity: 0, depthWrite: false, blending: THREE.AdditiveBlending });
  const mist = new THREE.Points(mGeo, mistMat); bottle.add(mist);
  let mi = 0;
  const emit = n => { for (let k = 0; k < n; k++) { const i = mi++ % MIST; mPos[i * 3] = 0; mPos[i * 3 + 1] = 1.3; mPos[i * 3 + 2] = .05; const a = Math.random() * Math.PI * 2, s = Math.random() * .5; mVel[i * 3] = Math.cos(a) * s * .6 + .15; mVel[i * 3 + 1] = 1.1 + Math.random() * 1.1; mVel[i * 3 + 2] = Math.sin(a) * s * .6 + .35; mLife[i] = 1; } };

  /* ---- choreography (per chapter) ---- */
  const K = [
    { x: 1.35, y: 0, ry: -.35, rx: .05, cap: 0, s: 1 },
    { x: -1.4, y: -.05, ry: .55, rx: .08, cap: 0, s: 1.02 },
    { x: 1.3, y: -.15, ry: -.1, rx: .12, cap: .95, s: 1.04 },
    { x: -1.35, y: 0, ry: .9, rx: .04, cap: 0, s: 1 }
  ];
  const KM = [ { x: 0, y: 1.05, ry: -.3, rx: .05, cap: 0, s: .6 }, { x: 0, y: 1.05, ry: .6, rx: .08, cap: 0, s: .6 }, { x: 0, y: .9, ry: 0, rx: .1, cap: .6, s: .62 }, { x: 0, y: 1.05, ry: -.9, rx: .04, cap: 0, s: .6 } ];

  let W = 0, H = 0;
  const resize = () => { const r = canvas.getBoundingClientRect(); W = r.width; H = r.height; renderer.setSize(W, H, false); camera.aspect = W / H; camera.updateProjectionMatrix(); };
  resize(); addEventListener('resize', resize);

  let mx = 0, my = 0, tmx = 0, tmy = 0;
  addEventListener('pointermove', e => { tmx = e.clientX / innerWidth - .5; tmy = e.clientY / innerHeight - .5; }, { passive: true });

  const progress = () => { const r = root.getBoundingClientRect(); const total = r.height - innerHeight; return clamp(-r.top / (total || 1)); };
  let visible = true;
  new IntersectionObserver(es => { visible = es[0].isIntersecting; }).observe(root);

  const clock = new THREE.Clock(); let sp = 0, intro = 0, spin = 0, lastCap = 0;
  const tint = new THREE.Color();
  root.classList.add('h3d--ready');

  renderer.setAnimationLoop(() => {
    if (!visible || document.hidden) { clock.getDelta(); return; }
    const dt = Math.min(clock.getDelta(), .05), t = clock.elapsedTime;
    sp = lerp(sp, progress(), .085);
    intro = Math.min(1, intro + dt * .55);
    mx = lerp(mx, tmx, .05); my = lerp(my, tmy, .05);

    const keys = small() ? KM : K, n = keys.length - 1, f = sp * n, i = Math.min(n - 1, Math.floor(f)), k = ease(clamp(f - i));
    const a = keys[i], b = keys[i + 1], v = {}; for (const p in a) v[p] = lerp(a[p], b[p], k);
    tint.copy(tints[Math.min(i, tints.length - 1)] || tints[0]).lerp(tints[Math.min(i + 1, tints.length - 1)] || tints[0], k);
    liquidMat.color.lerp(tint, .08); liquidMat.emissive.lerp(tint, .08);

    if (!reduce) spin += dt * .18;
    const ie = ease(intro);
    stage.position.set(v.x, v.y + (1 - ie) * -.6 + Math.sin(t * .8) * .04, 0);
    stage.scale.setScalar(v.s * lerp(.85, 1, ie));
    bottle.rotation.y = v.ry + Math.sin(spin) * .35 + mx * .5;
    bottle.rotation.x = v.rx + my * .18;
    bottle.position.y = .06 + Math.sin(t * 1.1) * .05;
    ped.rotation.y = -spin * .3;
    cap.position.y = 1.64 + v.cap * .95; cap.rotation.z = v.cap * .22; cap.position.x = v.cap * .55;

    if (v.cap > .6) emit(small() ? 2 : 4);
    if (v.cap > .6 && lastCap <= .6) emit(60);
    lastCap = v.cap;
    let alive = 0;
    for (let j = 0; j < MIST; j++) {
      if (mLife[j] <= 0) { mPos[j * 3 + 1] = -99; continue; }
      alive++; mLife[j] -= dt * .45;
      mPos[j * 3] += mVel[j * 3] * dt; mPos[j * 3 + 1] += mVel[j * 3 + 1] * dt; mPos[j * 3 + 2] += mVel[j * 3 + 2] * dt;
      mVel[j * 3] *= .985; mVel[j * 3 + 1] *= .975; mVel[j * 3 + 2] *= .985;
    }
    mGeo.attributes.position.needsUpdate = true;
    mistMat.opacity = lerp(mistMat.opacity, alive ? .16 : 0, .1);

    const dp = dGeo.attributes.position.array;
    for (let j = 0; j < DUST; j++) { dp[j * 3 + 1] += dt * (.05 + (dSeed[j] % 1) * .08); dp[j * 3] += Math.sin(t * .3 + dSeed[j]) * dt * .03; if (dp[j * 3 + 1] > 3.6) dp[j * 3 + 1] = -3.6; }
    dGeo.attributes.position.needsUpdate = true;
    dust.rotation.y = mx * .15;

    camera.position.x = lerp(camera.position.x, mx * .35, .05); camera.position.y = lerp(camera.position.y, .25 - my * .25, .05); camera.lookAt(0, .1, 0);
    arch.position.x = v.x * .55 - mx * .4; arch.position.y = -my * .2 + (small() ? v.y : 0);
    rim.intensity = 22 + Math.sin(t * 1.3) * 5;
    renderer.render(scene, camera);
  });
}
